import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  FileText, ArrowLeft, Download, Search, Edit, X, Save, 
  ChevronDown, Clock, Filter, Grid, List, Eye, Trash2,
  FileCheck, Calendar
} from 'lucide-react';
import { API } from '@/App';
import { toast } from 'sonner';
import Sidebar from '@/components/Sidebar';

const RunsPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [runs, setRuns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTemplate, setFilterTemplate] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [editingRun, setEditingRun] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(null);
  const [previewRun, setPreviewRun] = useState(null);

  useEffect(() => {
    const initData = async () => {
      // Fetch user data
      const token = localStorage.getItem('token');
      try {
        const userResponse = await fetch(`${API}/auth/me`, {
          credentials: 'include',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (userResponse.ok) {
          const userData = await userResponse.json();
          setUser(userData);
        }
      } catch (error) {
        console.error('Error fetching user:', error);
      }
      
      // Fetch runs and templates
      await fetchRuns();
      await fetchTemplates();
    };
    initData();
  }, []);

  const fetchRuns = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/runs`, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setRuns(data);
      }
    } catch (error) {
      toast.error('Failed to load runs');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/templates`, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return { relative: 'Recently', exact: '' };
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    const exact = date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    
    let relative;
    if (diffMins < 1) {
      relative = 'Just now';
    } else if (diffMins < 60) {
      relative = diffMins === 1 ? '1 min ago' : `${diffMins} mins ago`;
    } else if (diffHours < 24) {
      relative = diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
    } else if (diffDays === 1) {
      relative = 'Yesterday';
    } else if (diffDays < 7) {
      relative = `${diffDays} days ago`;
    } else {
      relative = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    }
    
    return { relative, exact };
  };

  const handleDownload = async (run, format = 'docx') => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/templates/${run.template_id}/export/${run.run_id}?format=${format}`, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to download document');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const filename = format === 'pdf' 
        ? run.generated_filename.replace('.docx', '.pdf')
        : run.generated_filename;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(`Downloaded as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleEdit = (run) => {
    setEditingRun(run);
    setEditFormData(run.field_data);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/runs/${editingRun.run_id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editFormData)
      });

      if (!response.ok) throw new Error('Failed to update run');

      const updatedRun = await response.json();
      setRuns(runs.map(r => r.run_id === updatedRun.run_id ? updatedRun : r));
      toast.success('Run updated successfully!');
      setEditingRun(null);
      setEditFormData({});
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (run) => {
    if (!confirm('Delete this run? This action cannot be undone.')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/runs/${run.run_id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to delete run');
      
      setRuns(runs.filter(r => r.run_id !== run.run_id));
      toast.success('Run deleted');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const filteredRuns = runs.filter(run => {
    const template = templates.find(t => t.template_id === run.template_id);
    const templateName = template?.name || '';
    const matchesSearch = templateName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      run.generated_filename.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTemplate = !filterTemplate || run.template_id === filterTemplate;
    return matchesSearch && matchesTemplate;
  });

  // Group runs by date
  const groupedRuns = filteredRuns.reduce((groups, run) => {
    const date = new Date(run.created_at).toLocaleDateString();
    if (!groups[date]) groups[date] = [];
    groups[date].push(run);
    return groups;
  }, {});

  // Unique templates that have runs
  const templatesWithRuns = [...new Set(runs.map(r => r.template_id))]
    .map(id => templates.find(t => t.template_id === id))
    .filter(Boolean);

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      <Sidebar user={user} />
      
      <div className="flex-1 flex flex-col lg:ml-64">
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-6xl mx-auto px-6 py-8 lg:pt-8 pt-20">{/* pt-20 for mobile header */}

      {/* Edit Modal */}
      {editingRun && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between">
              <h2 className="text-xl font-bold">Edit Run Data</h2>
              <button onClick={() => { setEditingRun(null); setEditFormData({}); }} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1 space-y-4">
              {Object.keys(editFormData).filter(k => !k.startsWith('system_')).map((fieldName) => {
                const template = templates.find(t => t.template_id === editingRun.template_id);
                const fieldConfig = template?.fields?.find(f => f.field_name === fieldName);
                const fieldType = fieldConfig?.field_type || 'text';
                
                return (
                  <div key={fieldName}>
                    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                      {fieldConfig?.label || fieldName.replace(/_/g, ' ')}
                    </Label>
                    {fieldType === 'textarea' ? (
                      <Textarea
                        value={editFormData[fieldName] || ''}
                        onChange={(e) => setEditFormData({ ...editFormData, [fieldName]: e.target.value })}
                        rows={3}
                        className="rounded-xl"
                      />
                    ) : (
                      <Input
                        type={fieldType}
                        value={editFormData[fieldName] || ''}
                        onChange={(e) => setEditFormData({ ...editFormData, [fieldName]: e.target.value })}
                        className="h-10 rounded-xl"
                      />
                    )}
                  </div>
                );
              })}
            </div>

            <div className="p-6 border-t border-slate-100 flex gap-3">
              <Button variant="outline" onClick={() => { setEditingRun(null); setEditFormData({}); }} className="flex-1 h-11 rounded-xl">
                Cancel
              </Button>
              <Button onClick={handleSaveEdit} disabled={saving} className="flex-1 h-11 rounded-xl bg-violet-600 hover:bg-violet-700 text-white">
                {saving ? 'Saving...' : <><Save className="w-4 h-4 mr-2" />Save Changes</>}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewRun && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between">
              <h2 className="text-xl font-bold">Field Values</h2>
              <button onClick={() => setPreviewRun(null)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <div className="space-y-3">
                {Object.entries(previewRun.field_data || {}).filter(([k]) => !k.startsWith('system_')).map(([key, value]) => {
                  const template = templates.find(t => t.template_id === previewRun.template_id);
                  const fieldConfig = template?.fields?.find(f => f.field_name === key);
                  return (
                    <div key={key} className="p-3 rounded-lg bg-slate-50">
                      <span className="text-xs font-medium text-slate-500 block mb-1">
                        {fieldConfig?.label || key.replace(/_/g, ' ')}
                      </span>
                      <span className="text-sm text-slate-900">{value || <span className="text-slate-400 italic">Empty</span>}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="p-4 border-t border-slate-100">
              <Button onClick={() => setPreviewRun(null)} variant="outline" className="w-full h-10 rounded-xl">
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Document Runs</h1>
            <p className="text-slate-500 mt-1">View, edit and download your generated documents</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-violet-100 text-violet-700' : 'text-slate-400 hover:text-slate-600'}`}
            >
              <List className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'bg-violet-100 text-violet-700' : 'text-slate-400 hover:text-slate-600'}`}
            >
              <Grid className="w-5 h-5" />
            </button>
          </div>
        </div>
          
          {/* Stats */}
          <div className="flex gap-6 mt-4">
            <div className="flex items-center gap-2 text-sm">
              <FileCheck className="w-4 h-4 text-green-600" />
              <span className="font-medium text-slate-900">{runs.length}</span>
              <span className="text-slate-500">total runs</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <FileText className="w-4 h-4 text-violet-600" />
              <span className="font-medium text-slate-900">{templatesWithRuns.length}</span>
              <span className="text-slate-500">templates used</span>
            </div>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search runs..."
              className="pl-10 h-10 rounded-xl"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <select
              value={filterTemplate}
              onChange={(e) => setFilterTemplate(e.target.value)}
              className="h-10 pl-10 pr-8 rounded-xl border border-slate-200 bg-white text-sm min-w-[180px] appearance-none"
            >
              <option value="">All Templates</option>
              {templatesWithRuns.map(t => (
                <option key={t.template_id} value={t.template_id}>{t.name}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-violet-600"></div>
          </div>
        ) : filteredRuns.length === 0 ? (
          <div className="text-center py-16">
            <FileText className="w-14 h-14 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">
              {searchQuery || filterTemplate ? 'No runs found' : 'No document runs yet'}
            </h3>
            <p className="text-slate-500 mb-6 max-w-md mx-auto">
              {searchQuery || filterTemplate
                ? 'Try adjusting your search or filter'
                : 'Run a template to generate your first document'}
            </p>
            {!searchQuery && !filterTemplate && (
              <Button onClick={() => navigate('/templates')} className="h-10 px-6 rounded-xl bg-violet-600 hover:bg-violet-700 text-white">
                Go to Templates
              </Button>
            )}
          </div>
        ) : viewMode === 'list' ? (
          /* List View */
          <div className="space-y-6">
            {Object.entries(groupedRuns).map(([date, dateRuns]) => (
              <div key={date}>
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  <span className="text-sm font-medium text-slate-500">{date}</span>
                  <span className="text-xs text-slate-400">({dateRuns.length} runs)</span>
                </div>
                <div className="space-y-2">
                  {dateRuns.map((run) => {
                    const template = templates.find(t => t.template_id === run.template_id);
                    const time = formatDate(run.created_at);
                    const fieldCount = Object.keys(run.field_data || {}).filter(k => !k.startsWith('system_')).length;
                    
                    return (
                      <div
                        key={run.run_id}
                        className="p-4 rounded-xl bg-white border border-slate-200 hover:border-slate-300 transition-colors group"
                        data-testid={`run-item-${run.run_id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                            <FileCheck className="w-5 h-5 text-green-600" />
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <h3 className="font-semibold text-slate-900 truncate">{template?.name || 'Unknown'}</h3>
                              <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-medium flex-shrink-0">
                                {run.status}
                              </span>
                            </div>
                            <div className="flex items-center gap-3 text-xs text-slate-500">
                              <span className="truncate">{run.generated_filename}</span>
                              <span className="text-slate-300">•</span>
                              <span title={time.exact} className="cursor-default flex items-center gap-1 flex-shrink-0">
                                <Clock className="w-3 h-3" />
                                {time.relative}
                              </span>
                              <span className="text-slate-300">•</span>
                              <span className="flex-shrink-0">{fieldCount} fields</span>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => setPreviewRun(run)}
                              className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-violet-600"
                              title="Preview data"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleEdit(run)}
                              className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-violet-600"
                              title="Edit"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(run)}
                              className="p-2 rounded-lg hover:bg-red-50 text-slate-500 hover:text-red-600"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                            
                            {/* Download Dropdown */}
                            <div className="relative">
                              <button
                                onClick={() => setShowDownloadMenu(showDownloadMenu === run.run_id ? null : run.run_id)}
                                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-medium"
                              >
                                <Download className="w-4 h-4" />
                                <ChevronDown className="w-3 h-3" />
                              </button>
                              
                              {showDownloadMenu === run.run_id && (
                                <>
                                  <div className="fixed inset-0 z-10" onClick={() => setShowDownloadMenu(null)} />
                                  <div className="absolute right-0 top-full mt-1 w-44 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-20">
                                    <button
                                      onClick={() => { handleDownload(run, 'docx'); setShowDownloadMenu(null); }}
                                      className="w-full px-4 py-2.5 text-left hover:bg-slate-50 flex items-center gap-2 text-sm"
                                    >
                                      <FileText className="w-4 h-4 text-blue-600" />
                                      Word (.docx)
                                    </button>
                                    <button
                                      onClick={() => { handleDownload(run, 'pdf'); setShowDownloadMenu(null); }}
                                      className="w-full px-4 py-2.5 text-left hover:bg-slate-50 flex items-center gap-2 text-sm border-t border-slate-100"
                                    >
                                      <FileText className="w-4 h-4 text-red-600" />
                                      PDF (.pdf)
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Grid View */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredRuns.map((run) => {
              const template = templates.find(t => t.template_id === run.template_id);
              const time = formatDate(run.created_at);
              const fieldEntries = Object.entries(run.field_data || {}).filter(([k]) => !k.startsWith('system_')).slice(0, 3);
              
              return (
                <div
                  key={run.run_id}
                  className="p-5 rounded-xl bg-white border border-slate-200 hover:shadow-lg transition-shadow"
                  data-testid={`run-card-${run.run_id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                      <FileCheck className="w-5 h-5 text-green-600" />
                    </div>
                    <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                      {run.status}
                    </span>
                  </div>
                  
                  <h3 className="font-semibold text-slate-900 mb-1 truncate">{template?.name || 'Unknown'}</h3>
                  <p className="text-xs text-slate-500 mb-3 truncate">{run.generated_filename}</p>
                  
                  {/* Preview of fields */}
                  <div className="space-y-1.5 mb-4">
                    {fieldEntries.map(([key, value]) => {
                      const fieldConfig = template?.fields?.find(f => f.field_name === key);
                      return (
                        <div key={key} className="flex items-center gap-2 text-xs">
                          <span className="text-slate-400 truncate w-20">{fieldConfig?.label || key}:</span>
                          <span className="text-slate-700 truncate flex-1">{value || '-'}</span>
                        </div>
                      );
                    })}
                    {Object.keys(run.field_data || {}).filter(k => !k.startsWith('system_')).length > 3 && (
                      <span className="text-xs text-slate-400">+{Object.keys(run.field_data).filter(k => !k.startsWith('system_')).length - 3} more fields</span>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                    <span className="text-xs text-slate-500 flex items-center gap-1" title={time.exact}>
                      <Clock className="w-3 h-3" />
                      {time.relative}
                    </span>
                    <div className="flex items-center gap-1">
                      <button onClick={() => setPreviewRun(run)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-violet-600">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleEdit(run)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-violet-600">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDownload(run, 'docx')} className="p-1.5 rounded-lg hover:bg-green-50 text-slate-400 hover:text-green-600">
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RunsPage;
